<template>
  <cv-text-input
    v-if="visible"
    ref="cvTextInput"
    :model-value="inputValue"
    :disabled="disabled"
    :label="label"
    :type="inputType"
    :password-visible="passwordVisible"
    :password-hide-label="passwordHideLabel"
    :password-show-label="passwordShowLabel"
    :placeholder="placeholder"
    :invalid-message="invalidMessage"
    v-bind="attrs"
    @keyup="handleKeyUp"
    @blur="handleBlur"
    @paste="handlePaste"
    @change="handleChange"
    @update:model-value="handleInput"
  />
</template>

<script setup lang="ts">
import TextMixin from "../mixin/text.mixin";
import { nextTick, onMounted, ref, useAttrs, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";

// Mixin
defineOptions({
  mixins: [TextMixin],
});

// Props
interface Props {
  inputType?: string;
  passwordVisible?: boolean;
  focus?: boolean;
  value?: string;
  errorMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
  inputType: "",
  passwordVisible: false,
  focus: false,
  value: "",
  errorMessage: "",
});

// Emits
const emit = defineEmits<{
  errorUpdated: [data: unknown];
  keyup: [data: unknown];
  blur: [data: unknown];
}>();

// Composables
const { vm } = useVm();
const { t } = useI18n();
const attrs = useAttrs();

// Data
const passwordHideLabel = ref(t("hide"));
const passwordShowLabel = ref(t("show"));

// Template refs
const cvTextInputRef = useTemplateRef<HTMLElement>("cvTextInput");

// Methods
const handleKeyUp = (e) => {
  emit("keyup", e);
};

const handleBlur = (e) => {
  emit("blur", e);
};

onMounted(() => {
  // Watch
  watch(
    () => props.errorMessage,
    (error) => {
      vm.invalidMessage = error;
    },
  );

  watch(
    () => vm.invalidMessage,
    () => {
      nextTick(() => {
        emit("errorUpdated", { code: "ok", data: vm.invalidMessage });
      });
    },
    { deep: true },
  );

  if (vm.characterLimit > 0) {
    vm.validateMaxLimit(); // Validate maximum limit for pre-defined text inputs
  }

  if (props.focus) {
    nextTick(() => {
      const textInput = cvTextInputRef.value;

      if (textInput) {
        textInput.$refs.input.focus();
      }
    });
  }
});

defineExpose({
  validate: () => vm.validate(),
  handleInput: (inputValue) => vm.handleInput(inputValue),
  handlePaste: (event) => vm.handlePaste(event),
  handleChange: () => vm.handleChange(),
  get value() {
    return props.value;
  },
});
</script>
